"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { webSiteNavigationLabels } from "../webSiteNavigation";
import { useWebMobileMenuOverlay } from "./useWebMobileMenuOverlay";

export function NorthlineMobileMenu({
    languageCode,
}: {
    languageCode?: string | null;
}) {
    const navigationId = useId();
    const sentinelRef = useRef<HTMLSpanElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const labels = webSiteNavigationLabels(languageCode);

    useEffect(() => {
        const template = sentinelRef.current?.closest<HTMLElement>(
            '[data-web-template="northline-stay"]',
        );
        const nextHeader = template?.querySelector<HTMLElement>(
            '[data-web-region="header"]',
        ) ?? null;
        const hero = template?.querySelector<HTMLElement>(
            '[data-web-region="homeHero"]',
        ) ?? null;

        setHeader(nextHeader);

        if (!template || !nextHeader) {
            return;
        }

        let animationFrame = 0;
        const updateHeader = () => {
            window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(() => {
                const threshold = window.matchMedia("(max-width: 900px)").matches
                    ? 54
                    : 110;

                nextHeader.toggleAttribute(
                    "data-northline-header-scrolled",
                    hero === null || window.scrollY > threshold,
                );
            });
        };

        updateHeader();
        window.addEventListener("resize", updateHeader, { passive: true });
        window.addEventListener("scroll", updateHeader, { passive: true });

        const sections = Array.from(template.querySelectorAll<HTMLElement>(
            '[data-builder-document-region="page"] .site-builder-section',
        ));
        const editorMode = template.dataset.webTemplateEditor === "true";
        const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        let observer: IntersectionObserver | null = null;

        sections.forEach((section, index) => {
            section.dataset.northlineReveal = "true";
            if (editorMode || reducedMotion || index === 0) {
                section.dataset.northlineRevealed = "true";
            }
        });

        if (!editorMode && !reducedMotion && "IntersectionObserver" in window) {
            observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    (entry.target as HTMLElement).dataset.northlineRevealed = "true";
                    observer?.unobserve(entry.target);
                });
            }, {
                rootMargin: "0px 0px -8%",
                threshold: 0.12,
            });

            sections.slice(1).forEach((section) => observer?.observe(section));
        } else {
            sections.forEach((section) => {
                section.dataset.northlineRevealed = "true";
            });
        }

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener("resize", updateHeader);
            window.removeEventListener("scroll", updateHeader);
            observer?.disconnect();
            nextHeader.removeAttribute("data-northline-header-scrolled");
        };
    }, []);

    useWebMobileMenuOverlay({
        header,
        isOpen,
        navigationId,
        openAttribute: "data-northline-mobile-menu-open",
        setIsOpen,
        templateCode: "northline-stay",
        toggleRef,
    });

    return (
        <>
            <span
                aria-hidden="true"
                data-northline-mobile-menu-runtime="true"
                ref={sentinelRef}
                style={{ display: "none" }}
            />
            {header
                ? createPortal(
                    <button
                        aria-controls={navigationId}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? labels.closeMenu : labels.openMenu}
                        className="northline-mobile-menu-toggle"
                        onClick={(event) => {
                            event.stopPropagation();
                            setIsOpen((current) => !current);
                        }}
                        onPointerDown={(event) => event.stopPropagation()}
                        ref={toggleRef}
                        type="button"
                    >
                        <span />
                        <span />
                    </button>,
                    header,
                )
                : null}
        </>
    );
}
